import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Building2,
  Heart,
  Users,
  Wallet,
  CalendarClock,
  Clock3,
  Store,
  FolderOpen,
  Palette,
  Bell,
  Settings,
  Receipt,
  Sparkles,
  Map,
} from "lucide-react";

export const APP_NAME = "Atelier Suite";
export const APP_TAGLINE = "Wedding planning, run as a business.";

export type UserRole = "owner" | "admin" | "planner" | "coordinator" | "client" | "vendor";

export const ROLE_LABELS: Record<UserRole, string> = {
  owner: "Owner",
  admin: "Admin",
  planner: "Planner",
  coordinator: "Coordinator",
  client: "Client",
  vendor: "Vendor",
};

export interface NavModule {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Modules not yet built ship as placeholders but stay visible in nav. */
  status: "live" | "upcoming";
  /**
   * "org" modules operate across every wedding in the active organization.
   * "wedding" modules operate on the single active wedding — these are the
   * ones surfaced as quick links from a wedding's detail page.
   */
  scope: "org" | "wedding";
  /** Roles allowed to see this module. Omit to allow all roles. */
  roles?: UserRole[];
}

/**
 * Single source of truth for primary navigation. Adding a real feature
 * later means flipping `status` to "live" and pointing the route at the
 * real feature module — no nav restructuring required.
 */
export const NAV_MODULES: NavModule[] = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, status: "live", scope: "org" },
  { label: "Organizations", path: "/organizations", icon: Building2, status: "live", scope: "org" },
  { label: "Weddings", path: "/weddings", icon: Heart, status: "live", scope: "org" },
  { label: "Vendor directory", path: "/vendors", icon: Store, status: "live", scope: "org" },
  { label: "Guests", path: "/guests", icon: Users, status: "live", scope: "wedding" },
  { label: "Budget", path: "/budget", icon: Wallet, status: "live", scope: "wedding" },
  { label: "Planning", path: "/planning", icon: CalendarClock, status: "live", scope: "wedding" },
  { label: "Run of Show", path: "/run-of-show", icon: Clock3, status: "live", scope: "wedding" },
  { label: "Event layout", path: "/layout", icon: Map, status: "live", scope: "wedding" },
  { label: "Vendors", path: "/vendors", icon: Store, status: "live", scope: "wedding" },
  { label: "Documents", path: "/documents", icon: FolderOpen, status: "live", scope: "wedding" },
  { label: "Moodboard", path: "/moodboard", icon: Palette, status: "live", scope: "wedding" },
  { label: "Notifications", path: "/notifications", icon: Bell, status: "live", scope: "org" },
  { label: "AI Assistant", path: "/ai-assistant", icon: Sparkles, status: "live", scope: "org" },
  { label: "Billing", path: "/billing", icon: Receipt, status: "live", scope: "org" },
  { label: "Settings", path: "/settings", icon: Settings, status: "live", scope: "org" },
];

/** Modules that operate on a single wedding — surfaced from the wedding detail page. */
export const WEDDING_SCOPED_MODULES = NAV_MODULES.filter((m) => m.scope === "wedding");

export const ORGANIZATION_MODULES = NAV_MODULES.filter((m) => m.scope === "org");

export function weddingModulePath(weddingId: string, modulePath = "") {
  return `/weddings/${encodeURIComponent(weddingId)}${modulePath}`;
}
